import { Component, signal, computed } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { PasswordModule } from "primeng/password";
import { Router } from "@angular/router";
import { finalize } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { IResetPassword_Request } from "../../../Utils/_services/account/account.model";
import { IApiError } from "../../../Utils/_interceptors/network-error.model";
import { AccountService } from "../../../Utils/_services/account/account.service";
import { LeftUiTemplateComponent } from "../left-ui-template/left-ui-template.component";

@Component({
  selector: "app-reset-password",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    PasswordModule,
    LeftUiTemplateComponent
],
  templateUrl: "./reset-password.component.html",
  styleUrl: "./reset-password.component.css",
})
export class ResetPasswordComponent {
  isProcessing = signal(false);
  newPassword = signal<string>("");
  confirmPassword = signal<string>("");
  showNewPassword = signal<boolean>(false);
  showConfirmPassword = signal<boolean>(false);
  year = signal(new Date().getFullYear());
  email = signal<string>("");
  code = signal<string>("");

  constructor(
    private router: Router,
    private accountService: AccountService,
    private toastr: ToastrService
  ) {
    const nav = this.router.getCurrentNavigation();
    const state = nav?.extras.state as any;

    if (state) {
      this.email.set(state.email);
      this.code.set(state.code);
    }

    if (!this.email() || !this.code()) {
      this.router.navigate(["/forgot-password"]);
    }
    history.replaceState({}, "");
  }

  // Password strength validation
  passwordStrength = computed(() => {
    const password = this.newPassword();
    let strength = 0;

    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return { label: "Weak", class: "weak", value: 33 };
    if (strength <= 4) return { label: "Medium", class: "medium", value: 66 };
    return { label: "Strong", class: "strong", value: 100 };
  });

  // Validation checks
  hasMinLength = computed(() => this.newPassword().length >= 8);
  hasUpperCase = computed(() => /[A-Z]/.test(this.newPassword()));
  hasLowerCase = computed(() => /[a-z]/.test(this.newPassword()));
  hasNumber = computed(() => /[0-9]/.test(this.newPassword()));
  hasSpecialChar = computed(() => /[^a-zA-Z0-9]/.test(this.newPassword()));
  passwordsMatch = computed(
    () =>
      this.newPassword() &&
      this.confirmPassword() &&
      this.newPassword() === this.confirmPassword()
  );

  isFormValid = computed(
    () =>
      this.hasMinLength() &&
      this.hasUpperCase() &&
      this.hasLowerCase() &&
      this.hasNumber() &&
      this.hasSpecialChar() &&
      this.passwordsMatch()
  );

  toggleNewPasswordVisibility() {
    this.showNewPassword.set(!this.showNewPassword());
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword.set(!this.showConfirmPassword());
  }

  resetPassword() {
    if (this.isFormValid()) {
      this.isProcessing.set(true);
      const req: IResetPassword_Request = {
        email: this.email(),
        verificationCode: this.code(),
        newPassword: this.newPassword(),
        confirmPassword: this.confirmPassword(),
      };
      this.accountService
        .resetPassword(req)
        .pipe(
          finalize(() => {
            this.isProcessing.set(false);
          })
        )
        .subscribe({
          next: (res) => {
            if (res.auth.success) {
              this.toastr.success("Passwored reseted succussfully");
              this.router.navigate(["/auth/login"]);
            }
          },
          error: (err: IApiError) => {
            this.toastr.error(
              "Something went wrong while reseting the password"
            );
          },
        });
    }
  }

  backToLogin() {
    this.router.navigate(["/auth/login"]);
  }
}
